import React, {useState} from 'react';
import {toEntityId} from '../../../../modals/time-registration/time-registration-modal/util/TimeRegistrationModalUtil';
import TimeRegistrationModalCreateQueryWrapper from '../../../../modals/time-registration/time-registration-modal/query-wrappers/TimeRegistrationModalCreateQueryWrapper';
import {Tabs, Scrollbar, Text, Heading, FlexRow, FlexColumn} from '@forecast-it/design-system';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import Util from '../../../../../util/util';
import TimeEntry from '../time_entry';
import {getLockedDate} from '../../../../../../../components/timesheets/time-lock/TimeLockUtil';
import moment from 'moment';
const TabsWrapper = styled.div`
	min-width: 452px;
	min-height: 400px;
	padding-top: 12px;
`;

const TabContentWrapper = styled.div`
	padding: 12px;
	height: 100%;
`;

type ExistingTimeRegType = {
	node: {
		id: string;
		minutesRegistered: number;
		person: {
			id: string;
		};
	};
};

type TimeRegistrationWithTimeRegListViewer = {
	id: string;
	actualPersonId: string;
	company: {
		id: string;
		useTimeApproval: boolean;
	};
	task: {
		id: string;
		project: {
			id: string;
		};
	};
};

const TimeEntryWrapper = styled.div`
	li.time-registration-line {
		border-top: none !important;
		margin-bottom: 12px !important;
		.forecast-person {
			max-width: 250px !important;
			width: 250px !important;
		}
	}
	.forecast-person .person-info .name {
		font-family: 'ABC Whyte', sans-serif;
		font-weight: 500;
		font-size: 16px;
	}
	.forecast-person .person-info .role {
		font-weight: 350;
		font-size: 14px;
	}
	.person-initials-hexagon-shape .initials {
		top: -5px;
	}
`;

const Paragraph = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 8px;
`;

const RightSection = styled.div<{height: number}>`
	display: flex;
	height: ${props => props.height + 'px'};
	width: 200px;
	padding: 24px 12px;
	background: ${props => props.theme.sys.color.background.neutral['2']};
	border-style: none none none solid;
	border-width: ${props => props.theme.sys.size.borderwidth.thin};
	border-color: ${props => props.theme.sys.color.border.decorative};
	border-radius: 0 ${props => props.theme.sys.size.borderradius['3']} ${props => props.theme.sys.size.borderradius['3']} 0;
`;

function SubtaskTimeRegSummary({
	existingTimeRegs,
	rollupTimeEntries,
	height,
}: {
	existingTimeRegs: ExistingTimeRegType[];
	rollupTimeEntries: number;
	height: number;
}) {
	const intl = useIntl();
	const totalMinutesRegistered = existingTimeRegs.reduce(
		(totalMinutesRegistered, timeRegistrationEdge) => totalMinutesRegistered + timeRegistrationEdge.node.minutesRegistered,
		0
	);
	const totalMinutesRollup = totalMinutesRegistered + (rollupTimeEntries ?? 0);
	return (
		<RightSection height={height}>
			<FlexColumn>
				<Heading size={4}>Time Entry Totals</Heading>
				<Paragraph>
					<Text>{intl.formatMessage({id: 'task_modal.current_task'})}</Text>
					<Text>
						<b>{Util.convertMinutesToFullHour(totalMinutesRegistered, intl, true)}</b>
					</Text>
				</Paragraph>

				<Paragraph>
					<Text>{intl.formatMessage({id: 'task_modal.including_subtasks'})}</Text>
					<Text>
						<b>{Util.convertMinutesToFullHour(totalMinutesRollup, intl, true)}</b>
					</Text>
				</Paragraph>
			</FlexColumn>
		</RightSection>
	);
}

const FlexRowWithHeight = styled(FlexRow)`
	max-height: ${props => props.height + 'px'};
	height: 100%;
`;

type HarvestTaskOption = {
	value: string;
	label: string;
};

export type TimeRegistrationWithTimeRegListProps = {
	viewer: TimeRegistrationWithTimeRegListViewer;
	taskId: string;
	existingTimeRegs: ExistingTimeRegType[];
	rollupTimeEntries: number;
	onDone: () => void;
	harvestTaskOptions: HarvestTaskOption[];
	lockedDate: moment.Moment;
	noNewTimeEntries: boolean;
	personProjectRoles: Map<string, string>;
	disabled: boolean;
	modalRef: React.RefObject<HTMLDivElement>;
	focusTimeEntriesTile: () => void;
	containerHeight: number;
};
const TimeRegistrationWithTimeRegList = ({
	viewer,
	taskId,
	existingTimeRegs,
	rollupTimeEntries,
	noNewTimeEntries,
	onDone,
	personProjectRoles,
	disabled,
	harvestTaskOptions,
	modalRef,
	focusTimeEntriesTile,
	containerHeight,
}: TimeRegistrationWithTimeRegListProps): React.JSX.Element => {
	const [selectedTab, setSelectedTab] = useState('new');
	const formatMessage = useIntl().formatMessage;
	enum TABS {
		NEW = 'new',
		EXISTING = 'existing',
	}
	return (
		<FlexRowWithHeight alignItems={'flex-start'} height={containerHeight}>
			<TabsWrapper>
				<Tabs value={selectedTab} onValueChange={setSelectedTab}>
					<Tabs.List>
						<Tabs.Tab data-cy={TABS.NEW + '-tab'} value={TABS.NEW}>
							New Time Entry
						</Tabs.Tab>
						<Tabs.Tab data-cy={TABS.EXISTING + '-tab'} value={TABS.EXISTING}>
							Existing Time Entries ({existingTimeRegs.length})
						</Tabs.Tab>
					</Tabs.List>
					<Tabs.Content value={TABS.NEW}>
						<TabContentWrapper>
							<TimeRegistrationModalCreateQueryWrapper
								personId={viewer.actualPersonId}
								showAsModal={false}
								fromTaskModal={true}
								entityId={toEntityId({idleTimeId: undefined, projectId: undefined, taskId: taskId})}
								closeModal={onDone}
								onCreateSuccessCallbacks={[onDone]}
							/>
						</TabContentWrapper>
					</Tabs.Content>
					<Tabs.Content value={TABS.EXISTING}>
						<TabContentWrapper>
							<Scrollbar
								maxHeight={containerHeight - 100}
								isPageScroller={false}
								hideAxis={Scrollbar.Axis.Horizontal}
								hasFocusableContent={false}
							>
								<div style={{padding: '2px'}}>
									{noNewTimeEntries && !existingTimeRegs.length ? (
										<div className="no-time-registered-text">
											{formatMessage({id: 'common.no_time_registered'})}
										</div>
									) : null}
									{existingTimeRegs.map(timeRegistrationEdge => {
										const timeRegPerson = timeRegistrationEdge.node.person;
										const personLockedDate = getLockedDate(viewer.company, timeRegPerson);
										return (
											<TimeEntryWrapper>
												<TimeEntry
													key={timeRegistrationEdge.node.id}
													viewer={viewer}
													personProjectRoles={personProjectRoles}
													task={viewer.task}
													timeReg={timeRegistrationEdge.node}
													disabled={disabled}
													focusTimeEntriesTile={focusTimeEntriesTile}
													harvestTaskOptions={harvestTaskOptions}
													modalRef={modalRef}
													lockedDate={personLockedDate}
													useTimeApproval={viewer.company.useTimeApproval}
												/>
											</TimeEntryWrapper>
										);
									})}
								</div>
							</Scrollbar>
						</TabContentWrapper>
					</Tabs.Content>
				</Tabs>
			</TabsWrapper>
			{selectedTab === TABS.EXISTING && rollupTimeEntries != null && (
				<SubtaskTimeRegSummary
					height={containerHeight - 2}
					existingTimeRegs={existingTimeRegs}
					rollupTimeEntries={rollupTimeEntries}
				/>
			)}
		</FlexRowWithHeight>
	);
};

export default TimeRegistrationWithTimeRegList;
